import { useEffect } from "react";
import { useAccount } from "../../context/AccountProvider";
import { useArrangement } from "../../context/ArrangementProvider";
import { useAuth } from "../../context/AuthProvider";
import { useSfs } from "../../context/SfsProvider";
import { useUtils } from "../../context/UtilsProvider";
import { getCpsBrowserData, mapFormToGlobalpay } from "./utils";
import {
  FormPaymentRequest,
  GlobalpayPaymentResponse,
  Threedsv2ChallengeResponse,
} from "./types";
import environment from "../../env";
import { useAnalytics } from "../../context/AnalyticsProvider";


export function useGlobalpayPaymentFlow() {
  const { setTag } = useAnalytics();
  const { publicIpAddress } = useUtils();
  const { useGetAccount } = useAccount();
  const { account, isAccountLoading } = useGetAccount();
  const { accessToken } = useAuth();
  const { useGetSfs, loadSfsData, sfsLoaded } = useSfs();
  const {
    useGetArrangement,
    useGetArrangementHistory,
    loadPlanData,
    planLoaded,
  } = useArrangement();
  const { sfs, isSfsLoading } = useGetSfs();
  const { arrangement, isArrangementLoading } = useGetArrangement();
  const { arrangementHistory, isArrangementHistoryLoading } =
    useGetArrangementHistory();
  const accountReference = account?.accountReference ?? "";
  const currentBalance = account?.currentBalance ?? 0;

  useEffect(() => {
    if (
      !isAccountLoading &&
      !isArrangementLoading &&
      !isArrangementHistoryLoading &&
      !planLoaded &&
      sfsLoaded
    ) {
      loadPlanData(arrangement, arrangementHistory);
    }
    if (!sfsLoaded && !isSfsLoading) {
      loadSfsData(sfs);
    }
  }, [
    planLoaded,
    isSfsLoading,
    sfsLoaded,
    isArrangementLoading,
    isArrangementHistoryLoading,
    loadPlanData,
    loadSfsData,
    arrangement,
    arrangementHistory,
    sfs,
    isAccountLoading,
  ]);

  const loadingContext =
    isAccountLoading ||
    isSfsLoading ||
    isArrangementLoading ||
    isArrangementHistoryLoading;

  const submitPayment = async (
    form: FormPaymentRequest
  ): Promise<GlobalpayPaymentResponse> => {
    const browserData = getCpsBrowserData();
    const challengeCallbackUrl = `${environment.CUSTOMER_PORTAL_API}/globalpay/callback`;
    const globalpayRequest = mapFormToGlobalpay(
      form,
      publicIpAddress,
      challengeCallbackUrl,
      browserData
    );
    const url = `${environment.CUSTOMER_PORTAL_API}/globalpay/payments`;
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
          "x-api-key": environment.CUSTOMER_PORTAL_API_KEY,
        },
        body: JSON.stringify(globalpayRequest),
      });
      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }
      const data = await res.json();
      if (data?.status === "Successful") {
        setTag("payment_success");
      }
      return data as GlobalpayPaymentResponse;
    } catch (error) {
      return { status: "Error" };
    }
  };

  const continuePayment = async ({
    cres,
    securityTransId,
    securityDataVersion,
    encryptedOriginalRequest,
  }: Threedsv2ChallengeResponse): Promise<GlobalpayPaymentResponse> => {
    const url = `${environment.CUSTOMER_PORTAL_API}/globalpay/continue-payments`;
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${accessToken}`,
          "x-api-key": environment.CUSTOMER_PORTAL_API_KEY,
        },
        body: JSON.stringify({
          threedsv2ChallengeResponse: {
            cres,
            securityTransId,
            securityDataVersion,
          },
          encryptedOriginalRequest,
        }),
      });
      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }
      const data = await res.json();
      if (data?.status === "Successful") {
        setTag("payment_success");
      }
      return data as GlobalpayPaymentResponse;
    } catch (error) {
      return { status: "Error" };
    }
  };

  return {
    accountReference,
    currentBalance,
    loadingContext,
    submitPayment,
    continuePayment,
  };
}
